/** @module form */

/**
 * Wrapper Klasse für den WYSIWYG Editor
 *
 * @property {Object} wysiwygEditor EditorInstance
 */
export class OpaccFormElementWysiwyg {
	constructor() {
        let wysiwygSelector = 'textarea[data-op-editor="wysiwyg"]';
        if (document.querySelector(wysiwygSelector)) {
            import('../../lib/editor/opacc.editor.js').then(() => {
                this.wysiwygEditor = window.opaccEditor;
                this.initEditor(wysiwygSelector);
            });
        }
	}

	/**
	 * Initialisierung des WYSIWYG Elementes
	 *
	 * @param {string} selector HTMLQuerySelector
	 */
	initEditor(selector) {
		// Editor must be removed before initialisation
        this.wysiwygEditor.remove(selector);
		const wysiwygEditorUrl = this.getEditorFileUrl();
        const fileTypes = { image: 1, file: 2, media: 3 };

		this.wysiwygEditor.init({
            selector: selector,
            language: this.getLanguage(),
            plugins: [
                'advlist', 'autolink', 'lists', 'link', 'image',
                'charmap', 'preview', 'anchor', 'pagebreak', 'searchreplace',
                'wordcount', 'visualblocks', 'visualchars', 'code', 'fullscreen', 'insertdatetime',
                'media', 'nonbreaking', 'table', 'directionality',
                'emoticons', 'quickbars',
            ],
            insertdatetime_formats: ['%d.%m.%Y', '%H:%M'],
            menubar: false,
            contextmenu: false,
            encoding: 'xml',
            tools: 'inserttable',
            toolbar:
                'undo redo | formatselect | bold italic | backcolor | alignleft aligncenter'
                + 'alignright alignjustify | bullist numlist outdent indent | link image media |'
                + 'preview print | removeformat | code',
            width: '100%',
            base_url: `//${wysiwygEditorUrl.host}${wysiwygEditorUrl.pathname}/lib/editor/`,
            language_url: `//${wysiwygEditorUrl.host}${wysiwygEditorUrl.pathname}/lib/editor/translations/${this.getLanguage()}.js`,
			promotion: false,
            extended_valid_elements: 'i[*]',
            license_key: 'gpl',

            init_instance_callback: (editor)  => {
				editor.targetElm.setAttribute('tabindex', '-1');
				let iframe = document.getElementById(editor.id + '_ifr');
				editor.dom.setAttrib(iframe, 'tabindex', 0);

				if (editor.targetElm.hasAttribute('disabled')) {
                    editor.mode.set('readonly');
					editor.editorContainer.classList.add('is-disabled');
					editor.contentDocument.firstElementChild.classList.add('is-disabled');
					let iframe = document.getElementById(editor.id + '_ifr');
					editor.dom.setAttrib(iframe, 'tabindex', '-1');
					editor.off();
				}
			},

			setup: (editor) => {
				editor.on('SaveContent', (ed) => {
                    // Augrund des HTML Encodings durch Aktivieren des application.cnf Parameters 'NoXSSValidation' => htmlEncodeFields()
					// und dem standartmässig eingeschaltetem forced_root_block: <p> beim initialisieren wird der folgende content zuerst
					// nochmals decodiert ( &lt;p&gt; => <p>) und anschliessend encodiert ( <p> => &lt;p&gt; ).
					ed.content = this.htmlEncode(this.htmlDecode(ed.content));
				});

				editor.on('blur keydown', (e) => {
					this.setContentToTextarea(editor, e);
				});
			},
            file_picker_callback: (callback, value, meta) => {
                let type = fileTypes[meta.filetype]
				window.tinymce.activeEditor.windowManager.openUrl({
                    title: 'Fileupload Area',
                    url: this.getFileManagerURL(window.tinymce.activeEditor, type),
                    width: 800,
                    height: 600,
                });
            },
		});
	}

    getFileManagerURL(editor, type) {
        return `/webportal/filemanager.aspx?editor=${editor.id}&type=${type}&lang=${this.getLanguage()}`;
    }

    /**
	 * WYSIWYG Content encoden
	 *
	 * @param {string} content
	 * @returns {string} content
	 */
    htmlEncode(content) {
        return content.toString().replace(/&/g, '&amp;').replace(/"/g, '&quot;').replace(/</g, '&lt;')
            .replace(/>/g, '&gt;');
    }

	/**
	 * WYSIWYG Content decoren
	 *
	 * @param {string} content
	 * @returns {string} content
	 */
	htmlDecode(content) {
		return content.toString().replace(/&gt;/g, '>').replace(/&lt;/g, '<').replace(/&quot;/g, '"')
            .replace(/&amp;/g, '&');
	}

	/**
	 * Ermittlung des aktuellen Dateipfades
	 *
	 * @returns {*} URLObject
	 */
	 getEditorFileUrl() {
		const editorUrl = import.meta !== undefined ? new URL(import.meta.url) : {};

        if ('pathname' in editorUrl) {
			for (let x = 0; x < 3; x++) {
				editorUrl.pathname = editorUrl.pathname.substring(0, editorUrl.pathname.lastIndexOf("/"));
			}
		} else {
			editorUrl.host = 'cdn.was.local';
			editorUrl.pathname = '/was/dev/src'; //Versionsnummer wird beim Deployment des CDN aus Bamboo-Variable übernommen
		}
		return editorUrl;
	}

	/**
	 * Bestimmung der aktuellen Sprache aus der URL
	 *
	 * @returns {string} locale
	 */
	getLanguage() {
		if (location.href.indexOf('/en/') !== -1) {
			return 'en_GB';
		} else if (location.href.indexOf('/fr/') !== -1) {
			return 'fr_FR';
		} else if (location.href.indexOf('/it/') !== -1) {
			return 'it';
		} else {
			return 'de';
		}
	}

	/**
	 * WYSIWYG Content in die entsprechende Textarea einfügen und Prüfung auf Eingabe
	 *
	 * @param {Object} ed active Editor instance
	 */
	setContentToTextarea(ed, e) {

		if (document.activeElement.tagName == 'IFRAME') {
			let keyCode = e.keyCode;
			if (keyCode === 9 || e.type == 'blur') {
				ed.save();
				ed.targetElm.focus();
				ed.targetElm.style.zIndex = '-1';
				ed.targetElm.style.display = 'block';
				ed.targetElm.blur();
				ed.targetElm.style.display = 'none';
			}
		}
	}
}
